/**
 * Which picture speaks for a deal.
 *
 * The dossier gathers photographs from four places and used to simply
 * concatenate them: deal-file attachments, then browser Places results, then
 * the server lookup. Street View is first *within* the server list and that
 * list went last, so the one source guaranteed to be pointed at the building
 * could never lead — and `photos[0]` is not merely the first tile, it is the
 * dossier's hero image and the deal's thumbnail everywhere it is listed.
 *
 * On live deals that put a resident's family snapshot on 5940 Singleton Rd and
 * the sponsor's letterhead logo on 46 Old Ferry Road.
 */

import type { PropertyPhoto } from '@/types/propertyProfile'

/**
 * Filenames a mail client invents for an inline image.
 *
 * Outlook names embedded signature graphics `image001.png` upward, and a
 * pasted logo commonly arrives as `attachment.png`. These are the sender's
 * branding, never a photograph of the asset.
 */
export const EMAIL_IMAGE_ARTIFACT =
  /^(image\d+|attachment|logo|signature|banner|icon|header|footer)[-_ ]?\d*\.[a-z]+$/i

/**
 * How strong this source's claim is that the picture shows *this building*.
 *
 * Ranked by what each source can actually promise, not by which answered
 * first:
 *
 *   Street View       Google's own camera, at the coordinate this deal
 *                     geocoded to. The one source guaranteed to be pointed at
 *                     the site.
 *   Property website  The building's own marketing photography, from a URL
 *                     recorded on the deal.
 *   Google Places     The right place, but user-contributed — as often a
 *                     resident's snapshot or a lobby interior as the exterior.
 *   Deal file         An image that happened to be attached to an email about
 *                     the deal. Sometimes the asset, often the sender's brand.
 */
export function photoRank(photo: PropertyPhoto): number {
  switch (photo.source) {
    case 'Street View':
      return 0
    case 'Property website':
      return 1
    case 'Google Places':
      return 2
    default:
      return EMAIL_IMAGE_ARTIFACT.test(photo.alt ?? '') ? 4 : 3
  }
}

/**
 * Strongest claim first, original order kept within a rank.
 *
 * Nothing is discarded. A photo that ranks last is still in the gallery with
 * its source shown on it; it just no longer speaks for the deal.
 */
export function orderPropertyPhotos(photos: PropertyPhoto[]): PropertyPhoto[] {
  return photos
    .map((photo, index) => ({ photo, index, rank: photoRank(photo) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.photo)
}
